/**
 * Deployment Banner - Shows environment indicator for blue-green deployments.
 *
 * Displays a banner at the top of the page when viewing the pre-production
 * (green) environment, helping users distinguish between prod and preprod.
 *
 * Uses CompactBanner as the base component with custom emerald styling.
 */
import { useQuery } from '@tanstack/react-query';
import { FlaskConical } from 'lucide-react';
import { CompactBanner } from '@/components/ui/BaseBanner';
import { api } from '@/lib/api';

interface DeploymentInfo {
  color: 'blue' | 'green';
  environment: 'production' | 'preprod';
}

export function DeploymentBanner() {
  const { data: deploymentInfo } = useQuery({
    queryKey: ['deployment-info'],
    queryFn: async (): Promise<DeploymentInfo> => {
      try {
        const { data } = await api.get<DeploymentInfo>('/deployment-info');
        // Validate response is actual JSON, not index.html from SPA fallback
        if (data && typeof data === 'object' && 'environment' in data) {
          return data;
        }
        return { color: 'blue', environment: 'production' };
      } catch {
        return { color: 'blue', environment: 'production' };
      }
    },
    staleTime: Infinity,
  });

  if (!deploymentInfo || deploymentInfo.environment === 'production') {
    return null;
  }

  return (
    <CompactBanner
      severity="info"
      message={
        <>
          <span className="font-medium">PRE-PRODUCTION</span>
          <span className="ml-2 opacity-75">Testing new code before it goes live</span>
        </>
      }
      icon={FlaskConical}
      className="bg-emerald-500 border-emerald-600 text-white rounded-none [&>svg]:text-white [&>span]:text-white"
    />
  );
}
